import { HttpsProxyAgent } from 'https-proxy-agent';
import { DSL_API_PATH, DSL_BASE_URL } from '../../../constants';
import HttpService from '../../../services/http-service/http-service';
import ServiceHandler from '../../../services/service-handler';
import { Item } from '../../pages/owner-manual/models/item';
import {
    LANG_CODES_CARIBBEAN,
    LANG_CODES_CENTRAL_AMERICA,
    OWNER_MANUALS,
    OWNER_MANUALS_LATAM_SPANISH,
} from '../../support-constants';

interface GetOwnerInformationByYearModelResult {
    ownerServiceResult: OwnerServiceResult[];
}

interface OwnerServiceResult {
    modelYear: number;
    model: string;
    matches: Matches;
}

interface GetOwnerInformationByVinResult extends OwnerServiceResult {
    saleDate?: string;
    buildDate?: string;
}

interface Matches {
    item: Item[];
}

export interface OwnerManualResponse {
    metadata: {
        displayModelName: string;
        modelYear: string;
        seoName?: string;
        error: Error | null;
    };

    [category: number]: Item[];
}

export interface OwnerManualArticleByModelYear {
    getOwnerInformationByYearModelResult: GetOwnerInformationByYearModelResult;
}

export interface OwnerManualArticleByVin {
    getOwnerInformationByVinResult: GetOwnerInformationByVinResult;
}

export interface OmAiSearchHealthCheck {
    isAiSearchUp: boolean;
}

export interface OmAiSearchResultResponse {
    session_id?: string;
    question_id?: number;
    answer?: string;
    rephrased_question?: string;
    gen_answer_timestamp?: string;
    sources?: string[];
    message?: string;
}

export interface OmAiSearchRequest {
    question: string;
    vehicle_model_name: string;
    vehicle_model_year: string;
    chat_history?: [];
    session_id?: string;
    locale?: string;
    vin?: string;
    user_id?: string;
}

export interface OmAiSearchFeedbackRequest {
    question_id: number | null;
    session_id: string;
    thumbs_up_down: string;
    comment: string;
    user_id: string;
}

export interface OmAiSearchFeedbackResponse {
    details: string;
}

const transformResponse = (response: OwnerServiceResult) => {
    const categories = new Map<number, Item[]>();

    const obj: OwnerManualResponse = {
        metadata: {
            displayModelName: '',
            modelYear: '',
            error: null,
        },
    };
    if (response.matches?.item.length < 1) {
        obj.metadata.error = new Error('No manuals returned');
    }
    response.matches?.item.forEach(item => {
        const items = categories.get(item.categoryId) || [];
        items.push(item);
        categories.set(item.categoryId, items);
    });
    categories.forEach(function(value, key) {
        obj[key] = value;
    });
    return obj;
};

const httpsAgent = new HttpsProxyAgent(
    process.env.http_proxy || 'http://internet.ford.com:83'
);

const {
    current3LetterCountryCode,
    currentLanguageRegionCode,
} = ServiceHandler.AppConfigurationService;

const map3LetterCountryCode = (currentLanguageRegionCode: string) => {
    if (OWNER_MANUALS.ENGLISH_WITH_DDMMYYY.includes(currentLanguageRegionCode))
        return 'GBR';
    else if (
        LANG_CODES_CENTRAL_AMERICA.CENTRAL_AMERICA_MARKETS_SPANISH.includes(
            currentLanguageRegionCode
        )
    )
        return 'PAN';
    else if (
        LANG_CODES_CARIBBEAN.CARIBBEAN_MARKETS_ENGLISH.includes(
            currentLanguageRegionCode
        )
    )
        return 'BHS';
    else if (
        LANG_CODES_CARIBBEAN.CARIBBEAN_MARKETS_SPANISH.includes(
            currentLanguageRegionCode
        )
    )
        return 'DOM';
    else return current3LetterCountryCode;
};

const mapCurLandRegCode = (currentLanguageRegionCode: string) => {
    if (
        LANG_CODES_CENTRAL_AMERICA.CENTRAL_AMERICA_MARKETS_SPANISH.includes(
            currentLanguageRegionCode
        ) ||
        LANG_CODES_CARIBBEAN.CARIBBEAN_MARKETS_SPANISH.includes(
            currentLanguageRegionCode
        ) ||
        OWNER_MANUALS_LATAM_SPANISH.includes(currentLanguageRegionCode)
    )
        return 'ES-MX';
    else if (
        LANG_CODES_CARIBBEAN.CARIBBEAN_MARKETS_ENGLISH.includes(
            currentLanguageRegionCode
        )
    )
        return 'EN-US';
    else return currentLanguageRegionCode.toUpperCase();
};
const countryCode = map3LetterCountryCode(currentLanguageRegionCode);
const languageRegionCode = mapCurLandRegCode(currentLanguageRegionCode);

export class OwnerManualService {
    public async getOwnerManualByModelYear(
        model: string,
        year: string,
        currentLanguageRegionCode?: string
    ): Promise<OwnerManualResponse> {
        const params = {
            country: countryCode.toUpperCase(),
            language: currentLanguageRegionCode
                ? currentLanguageRegionCode
                : languageRegionCode,
            model,
            year,
        };

        const url = DSL_BASE_URL + DSL_API_PATH.OWNER_MANUALS;
        return await HttpService.get<OwnerManualArticleByModelYear>(url, true, {
            httpsAgent,
            params,
        })
            .then(response => {
                const ownerServiceResult =
                    response.data.getOwnerInformationByYearModelResult
                        .ownerServiceResult;
                if (ownerServiceResult.length < 1) {
                    return {
                        metadata: {
                            error: new Error('No manuals returned'),
                        },
                    } as OwnerManualResponse;
                }
                return transformResponse(ownerServiceResult[0]);
            })
            .catch(error => {
                console.error(error.message);
                return { metadata: { error: error } } as OwnerManualResponse;
            });
    }

    public async getOwnerManualByVin(
        vin: string,
        modelName?: string,
        currentLanguageRegionCode?: string
    ): Promise<OwnerManualResponse> {
        const consumerKeyHeader = HttpService.getAppIdRequestHeaders()[
            'Consumer-Key'
        ];
        const headers = {
            vin: vin,
            'Consumer-Key': consumerKeyHeader,
        };
        const currentRegionCode = currentLanguageRegionCode
            ? currentLanguageRegionCode
            : languageRegionCode;

        const url = `${DSL_BASE_URL +
            DSL_API_PATH.OWNERS_MANUAL_VIN_V3}?country=${countryCode.toUpperCase()}&language=${currentRegionCode}&modelName=${modelName}`;

        return await HttpService.get<OwnerManualArticleByVin>(url, true, {
            headers,
        })
            .then(response => {
                return transformResponse(
                    response.data.getOwnerInformationByVinResult
                );
            })
            .catch(error => {
                console.error(error.message);
                return { metadata: { error: error } } as OwnerManualResponse;
            });
    }

    public async aiSearchHealthCheck(): Promise<OmAiSearchHealthCheck> {
        return { isAiSearchUp: true } as OmAiSearchHealthCheck;
    }

    public async getAiSearchResult(
        omiSearchRequest: OmAiSearchRequest
    ): Promise<OmAiSearchResultResponse> {
        const url = `${DSL_BASE_URL + DSL_API_PATH.AI_SEARCH}`;

        return await HttpService.post<any>(url, omiSearchRequest, {})
            .then(response => {
                return response.data;
            })
            .catch(error => {
                console.error('Error in AI search', error);
            });
    }

    public async getAiSearchFeedback(
        OmAiSearchFeedback: OmAiSearchFeedbackRequest
    ): Promise<OmAiSearchFeedbackResponse> {
        const url = `${DSL_BASE_URL + DSL_API_PATH.AI_SEARCH_FEEDBACK}`;

        return await HttpService.post<any>(url, OmAiSearchFeedback, {})
            .then(response => {
                return response.data;
            })
            .catch(error => {
                console.error('Error in AI search', error);
            });
    }
}

const ownerManualService = new OwnerManualService();
export default ownerManualService;
